import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X as XIcon, Bell as BellIcon, ChevronLeft, ChevronRight, Sparkles as SparklesIcon, Search as SearchIcon, CheckCircle2 } from "lucide-react";
import {
  getContactDisplayName,
  getContactLocation,
  getContactSource,
  getFirstContactDate,
  Icons,
} from "../../../../views/crm-pages/conversation/constant";
import {
  ConversationInsights,
  ConversationNote,
  DealProgress,
  DealStage,
  InsightsPanelProps,
  InsightTab,
} from "../../../../views/crm-pages/conversation/types";
import { GreenDot } from "./avatar";
import useCrmApi from "../../../hook/crm-req";
import { formatDateTime } from "../../../../utils/function";
import { TeamDetails } from "../../customer/types";
import Select from "../../../select";

const NOTE_MAX_LENGTH = 300;

// New Note modal — shared by every surface that renders <ActionsTab>
// (inbox InsightsPanel, archive preview modal, Leads "View Lead Details" popup).
const NewNoteModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onSave: (text: string) => Promise<boolean>;
}> = ({ isOpen, onClose, onSave }) => {
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) setText("");
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSave = async () => {
    if (!text.trim() || saving) return;
    setSaving(true);
    const ok = await onSave(text.trim());
    setSaving(false);
    if (ok) onClose();
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-sm p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-1">
          <h3 className="text-base font-bold text-slate-800 dark:text-gray-100">New Note</h3>
          <button
            onClick={onClose}
            className="p-1 rounded-full text-slate-400 dark:text-gray-500 hover:text-slate-600 dark:hover:text-gray-300 hover:bg-slate-100 dark:hover:bg-gray-700 bg-transparent border-none cursor-pointer"
          >
            <XIcon size={16} />
          </button>
        </div>
        <p className="text-xs text-slate-400 dark:text-gray-500 mb-4">Add contact notes for your reference</p>

        <label className="block text-xs font-semibold text-slate-600 dark:text-gray-400 mb-1.5">Description</label>
        <textarea
          value={text}
          maxLength={NOTE_MAX_LENGTH}
          onChange={(e) => setText(e.target.value)}
          placeholder="Type your note here. Add any important context about this contact."
          rows={4}
          className="w-full border border-slate-200 dark:border-gray-700 dark:bg-gray-900 rounded-xl px-3 py-2.5 text-sm text-slate-700 dark:text-gray-200 outline-none focus:border-indigo-400 resize-none"
        />
        <p className="text-[11px] text-slate-400 dark:text-gray-500 mt-1 mb-4">{text.length}/{NOTE_MAX_LENGTH} Characters</p>

        <button
          onClick={handleSave}
          disabled={!text.trim() || saving}
          className="w-full py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? "Saving…" : "Save Note"}
        </button>
      </div>
    </div>
  );
};

const MAX_TAGS = 10;

// New Tag modal — replaces typing straight into an inline input. Shown
// wherever a caller passes real, editable tags + onSaveTags (e.g. the Leads
// "View Lead Details" popup); read-only surfaces (plain conversation
// insights) never render the trigger for this at all.
// GET /tags — the tenant's full tag catalog, so a user can pick an existing
// tag instead of retyping one that (with a typo, different casing, etc.)
// would otherwise create a near-duplicate. Shared by every surface that
// renders <NewTagModal> (inbox insights, Leads' "View Lead Details" popups).
const getTagLabel = (item: any): string =>
  typeof item === "string" ? item : String(item?.name ?? item?.tag ?? item?.label ?? item?.title ?? "");

function useTagCatalog(isOpen: boolean) {
  const token = localStorage.getItem("authToken");
  const [allTags, setAllTags] = useState<string[]>([]);
  const [loadingTags, setLoadingTags] = useState(false);

  const { makeRequest: getTags } = useCrmApi("/tags", "GET", { Authorization: `Bearer ${token}` });

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoadingTags(true);
    (async () => {
      try {
        const [response] = await getTags();
        const raw: any[] = Array.isArray(response)
          ? response
          : Array.isArray(response?.results)
            ? response.results
            : Array.isArray(response?.data)
              ? response.data
              : Array.isArray(response?.tags)
                ? response.tags
                : [];
        if (!cancelled) {
          setAllTags([...new Set(raw.map(getTagLabel).filter(Boolean))]);
        }
      } catch {
        if (!cancelled) setAllTags([]);
      } finally {
        if (!cancelled) setLoadingTags(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return { allTags, loadingTags };
}

export const NewTagModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  existingTags: string[];
  onSave: (tags: string[]) => Promise<boolean>;
}> = ({ isOpen, onClose, existingTags, onSave }) => {
  const [draftTags, setDraftTags] = useState<string[]>(existingTags);
  const [input, setInput] = useState("");
  const [tagSearch, setTagSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const { allTags, loadingTags } = useTagCatalog(isOpen);

  useEffect(() => {
    if (isOpen) {
      setDraftTags(existingTags);
      setInput("");
      setTagSearch("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const addTag = (tag: string) => {
    const trimmed = tag.trim();
    if (!trimmed || draftTags.includes(trimmed) || draftTags.length >= MAX_TAGS) return;
    setDraftTags((prev) => [...prev, trimmed]);
  };

  const removeTag = (tag: string) => setDraftTags((prev) => prev.filter((t) => t !== tag));

  // Existing-tag rows toggle membership directly — click to add, click again
  // (or the checkmark) to remove — same interaction as picking from a list
  // of agents, just multi-select instead of single.
  const toggleExistingTag = (tag: string) => {
    if (draftTags.includes(tag)) removeTag(tag);
    else addTag(tag);
  };

  const filteredExistingTags = allTags.filter((t) =>
    t.toLowerCase().includes(tagSearch.trim().toLowerCase())
  );

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    const ok = await onSave(draftTags);
    setSaving(false);
    if (ok) onClose();
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-sm p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-1">
          <h3 className="text-base font-bold text-slate-800 dark:text-gray-100">New Tag</h3>
          <button
            onClick={onClose}
            className="p-1 rounded-full text-slate-400 dark:text-gray-500 hover:text-slate-600 dark:hover:text-gray-300 hover:bg-slate-100 dark:hover:bg-gray-700 bg-transparent border-none cursor-pointer"
          >
            <XIcon size={16} />
          </button>
        </div>
        <p className="text-xs text-slate-400 dark:text-gray-500 mb-4">Tag this conversation</p>

        <label className="block text-xs font-semibold text-slate-600 dark:text-gray-400 mb-1.5">Add New Tags</label>
        <div className="flex flex-wrap items-center gap-1.5 border border-slate-200 dark:border-gray-700 rounded-xl px-2.5 py-2 min-h-[42px] focus-within:border-indigo-400">
          {draftTags.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded bg-indigo-50 dark:bg-indigo-500/15 text-indigo-600 dark:text-indigo-400 border border-indigo-100 dark:border-indigo-500/30"
            >
              {tag}
              <button
                onClick={() => removeTag(tag)}
                className="text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 bg-transparent border-none cursor-pointer p-0 flex items-center"
              >
                <XIcon size={11} />
              </button>
            </span>
          ))}
          {draftTags.length < MAX_TAGS && (
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addTag(input);
                  setInput("");
                }
              }}
              placeholder={draftTags.length === 0 ? "Type a tag and press Enter" : ""}
              className="flex-1 min-w-[80px] text-xs outline-none border-none bg-transparent text-slate-700 dark:text-gray-200"
            />
          )}
        </div>
        <p className="text-[11px] text-slate-400 dark:text-gray-500 mt-1 mb-4">{draftTags.length}/{MAX_TAGS} Tags</p>

        <label className="block text-xs font-semibold text-slate-600 dark:text-gray-400 mb-1.5">
          Or pick from existing tags
        </label>
        <div className="relative mb-2">
          <SearchIcon size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 dark:text-gray-500" />
          <input
            value={tagSearch}
            onChange={(e) => setTagSearch(e.target.value)}
            placeholder="Search tags"
            className="w-full pl-7 pr-2.5 py-2 border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-slate-700 dark:text-gray-200 rounded-lg text-xs outline-none focus:border-indigo-400"
          />
        </div>
        <div className="max-h-32 overflow-y-auto mb-4 border border-slate-100 dark:border-gray-800 rounded-lg">
          {loadingTags ? (
            <p className="px-3 py-3 text-xs text-slate-400 dark:text-gray-500">Loading tags…</p>
          ) : filteredExistingTags.length === 0 ? (
            <p className="px-3 py-3 text-xs text-slate-400 dark:text-gray-500">
              {allTags.length === 0 ? "No existing tags yet" : "No matching tags"}
            </p>
          ) : (
            filteredExistingTags.map((tag) => {
              const selected = draftTags.includes(tag);
              return (
                <button
                  key={tag}
                  type="button"
                  onClick={() => toggleExistingTag(tag)}
                  disabled={!selected && draftTags.length >= MAX_TAGS}
                  className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-xs text-left border-none cursor-pointer transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                    selected
                      ? "bg-indigo-50 dark:bg-indigo-500/15 text-indigo-700 dark:text-indigo-400 font-medium"
                      : "bg-white dark:bg-gray-900 text-slate-700 dark:text-gray-300 hover:bg-slate-50 dark:hover:bg-gray-800"
                  }`}
                >
                  <span className="truncate">{tag}</span>
                  {selected && <CheckCircle2 size={14} className="text-emerald-500 shrink-0" />}
                </button>
              );
            })
          )}
        </div>

        <button
          onClick={handleSave}
          disabled={saving}
          className="w-full py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? "Saving…" : "Save Tags"}
        </button>
      </div>
    </div>
  );
};

// GET /conversations/{id}/insights — shared by every surface that shows
// insights (inbox InsightsPanel, archive preview modal, Leads' "View Lead
// Details" drawer) so they all show the exact same real data from the same
// endpoint. Always /conversations — there's no separate /leads/{id}/insights
// on the backend, so `id` here needs to be a real conversation id even when
// the caller is on the Leads page.
export function useConversationInsights(id?: string | null) {
  const token = localStorage.getItem("authToken");
  const [insights, setInsights] = useState<ConversationInsights | null>(null);
  const [loadingInsights, setLoadingInsights] = useState(false);

  const { makeRequest: getInsights } = useCrmApi(
    `/conversations`,
    "GET",
    { Authorization: `Bearer ${token}` },
  );

  useEffect(() => {
    if (!id) {
      setInsights(null);
      return;
    }
    let cancelled = false;
    setLoadingInsights(true);
    setInsights(null);
    (async () => {
      const [response, status] = await getInsights(undefined, undefined, `${id}/insights`);
      if (cancelled) return;
      setInsights(status >= 200 && status < 300 ? response : null);
      setLoadingInsights(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return { insights, loadingInsights };
}

export const DEAL_STAGE_ORDER: DealStage[] = ["new_lead", "contacted", "proposal_shared", "deal_won"];
export const DEAL_STAGE_LABELS: Record<DealStage, string> = {
  new_lead: "New Lead",
  contacted: "Contacted",
  proposal_shared: "Proposal Shared",
  deal_won: "Deal Won",
};

// The header banner's stage pill — "Deal Won / Proposal Shared / Contacted /
// New Lead" — same PATCH /conversations/{id}/deal-progress as the Details
// tab's stage timeline, just editable from a dropdown instead of clicking a
// dot. Shared by every surface that renders a lead/conversation header.
export const DealStageDropdown: React.FC<{
  currentStage?: DealStage | null;
  updating: boolean;
  onChange: (stage: DealStage) => void;
}> = ({ currentStage, updating, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!currentStage) return null;

  return (
    <div className="relative shrink-0" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        disabled={updating}
        className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-blue-50 dark:bg-blue-500/15 text-blue-700 dark:text-blue-400 border border-blue-100 dark:border-blue-500/30 hover:bg-blue-100 dark:hover:bg-blue-500/25 transition-colors disabled:opacity-60"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-current" />
        {updating ? "Saving…" : DEAL_STAGE_LABELS[currentStage]}
        <Icons.ChevronDown size={13} />
      </button>
      {open && (
        <div className="absolute right-0 z-20 mt-1 w-40 overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg">
          {DEAL_STAGE_ORDER.map((stage) => (
            <button
              key={stage}
              type="button"
              onClick={() => { onChange(stage); setOpen(false); }}
              className={`block w-full px-3.5 py-2 text-left text-xs font-medium hover:bg-gray-50 dark:hover:bg-gray-700 ${
                stage === currentStage ? "bg-blue-50 dark:bg-blue-500/15 text-blue-700 dark:text-blue-400" : "text-gray-700 dark:text-gray-300"
              }`}
            >
              {DEAL_STAGE_LABELS[stage]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// GET/PATCH /conversations/{id}/deal-progress — `fallbackStage`/`fallbackPercent`
// (the conversation's own `deal_stage`/`deal_progress_percent`) let the panel
// render a stage immediately, before this dedicated call resolves.
export function useDealProgress(
  convId?: string | null,
  fallbackStage?: DealStage,
  fallbackPercent?: number,
) {
  const token = localStorage.getItem("authToken");
  const [dealProgress, setDealProgress] = useState<DealProgress | null>(null);
  const [loadingDealProgress, setLoadingDealProgress] = useState(false);
  const [updatingStage, setUpdatingStage] = useState(false);

  const { makeRequest: getDealProgress } = useCrmApi("/conversations", "GET", { Authorization: `Bearer ${token}` });
  const { makeRequest: patchDealProgress } = useCrmApi("/conversations", "PATCH", { Authorization: `Bearer ${token}` });

  useEffect(() => {
    if (!convId) {
      setDealProgress(null);
      return;
    }
    let cancelled = false;
    setLoadingDealProgress(true);
    (async () => {
      const [response, status] = await getDealProgress(undefined, undefined, `${convId}/deal-progress`);
      if (cancelled) return;
      setDealProgress(status >= 200 && status < 300 ? response : null);
      setLoadingDealProgress(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convId]);

  const updateStage = async (stage: DealStage): Promise<boolean> => {
    if (!convId) return false;
    setUpdatingStage(true);
    const [response, status] = await patchDealProgress({ stage }, undefined, `${convId}/deal-progress`);
    setUpdatingStage(false);
    if (status >= 200 && status < 300) {
      setDealProgress(response);
      return true;
    }
    toast.error(response?.detail || response?.message || "Failed to update deal stage");
    return false;
  };

  const effective: DealProgress | null =
    dealProgress ||
    (convId && fallbackStage
      ? {
          conversation_id: convId,
          current_stage: fallbackStage,
          progress_percent: fallbackPercent ?? 0,
          updated_at: "",
          stages: DEAL_STAGE_ORDER.map((key, i) => ({
            key,
            label: DEAL_STAGE_LABELS[key],
            progress_percent: (i + 1) * 25,
            completed: DEAL_STAGE_ORDER.indexOf(fallbackStage) >= i,
            current: fallbackStage === key,
          })),
          history: [],
        }
      : null);

  return { dealProgress: effective, loadingDealProgress, updatingStage, updateStage };
}

// GET/POST/PATCH/DELETE /conversations/{id}/notes[/{noteId}]
export function useConversationNotes(convId?: string | null) {
  const token = localStorage.getItem("authToken");
  const [notes, setNotes] = useState<ConversationNote[]>([]);
  const [totalNotes, setTotalNotes] = useState(0);
  const [loadingNotes, setLoadingNotes] = useState(false);

  const { makeRequest: getNotes } = useCrmApi("/conversations", "GET", { Authorization: `Bearer ${token}` });
  const { makeRequest: postNote } = useCrmApi("/conversations", "POST", { Authorization: `Bearer ${token}` });
  const { makeRequest: patchNote } = useCrmApi("/conversations", "PATCH", { Authorization: `Bearer ${token}` });
  const { makeRequest: deleteNoteReq } = useCrmApi("/conversations", "DELETE", { Authorization: `Bearer ${token}` });

  useEffect(() => {
    if (!convId) {
      setNotes([]);
      setTotalNotes(0);
      return;
    }
    let cancelled = false;
    setLoadingNotes(true);
    (async () => {
      const [response, status] = await getNotes(undefined, { limit: 50, offset: 0 }, `${convId}/notes`);
      if (cancelled) return;
      if (status >= 200 && status < 300 && response) {
        setNotes(response.results || []);
        setTotalNotes(response.total ?? (response.results || []).length);
      }
      setLoadingNotes(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convId]);

  const addNote = async (description: string): Promise<boolean> => {
    if (!convId) return false;
    const [response, status] = await postNote({ description }, undefined, `${convId}/notes`);
    if (status >= 200 && status < 300 && response?.id) {
      setNotes((prev) => [response, ...prev]);
      setTotalNotes((prev) => prev + 1);
      return true;
    }
    toast.error(response?.detail || response?.message || "Failed to save note.");
    return false;
  };

  const editNote = async (noteId: string, description: string): Promise<boolean> => {
    if (!convId) return false;
    const [response, status] = await patchNote({ description }, undefined, `${convId}/notes/${noteId}`);
    if (status >= 200 && status < 300) {
      setNotes((prev) => prev.map((n) => (n.id === noteId ? { ...n, ...(response?.id ? response : { description }) } : n)));
      return true;
    }
    toast.error(response?.detail || response?.message || "Failed to update note.");
    return false;
  };

  const deleteNote = async (noteId: string): Promise<boolean> => {
    if (!convId) return false;
    const [response, status] = await deleteNoteReq(undefined, undefined, `${convId}/notes/${noteId}`);
    if (status >= 200 && status < 300) {
      setNotes((prev) => prev.filter((n) => n.id !== noteId));
      setTotalNotes((prev) => Math.max(0, prev - 1));
      return true;
    }
    toast.error(response?.detail || response?.message || "Failed to delete note.");
    return false;
  };

  return { notes, totalNotes, loadingNotes, addNote, editNote, deleteNote };
}

// ── Collapsible Section ──────────────────────────────────────────────────────

const CollapsibleSection: React.FC<{
  title: string;
  icon?: React.ReactNode;
  defaultOpen?: boolean;
  children: React.ReactNode;
}> = ({ title, icon, defaultOpen = true, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-slate-100 dark:border-gray-800">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 bg-transparent border-none cursor-pointer"
      >
        <span className="flex items-center gap-1.5 font-semibold text-sm text-slate-800 dark:text-gray-100">
          {icon}
          {title}
        </span>
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          className={`text-slate-400 dark:text-gray-500 transition-transform duration-200 ${open ? "rotate-0" : "-rotate-90"}`}
        >
          <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && <div className="px-4 pb-3">{children}</div>}
    </div>
  );
};

// Compact rendering for a { total, completed, active } breakdown, e.g. from
// engagement.session_metrics.customer_initiated / business_initiated.
const formatSessionBreakdown = (b?: { total: number; completed: number; active: number } | null): string => {
  if (!b) return "N.A.";
  return `${b.total} total · ${b.completed} completed · ${b.active} active`;
};

const Row: React.FC<{
  label: string;
  value?: string;
  valueColor?: string;
  valueEl?: React.ReactNode;
}> = ({ label, value, valueColor, valueEl }) => (
  <div className="flex items-center justify-between py-1.5 gap-2">
    <span className="text-xs text-slate-400 dark:text-gray-500 flex-shrink-0">{label}:</span>
    {valueEl ?? (
      <span
        className={`text-xs font-medium text-right truncate ${valueColor ? "" : "text-slate-800 dark:text-gray-100"}`}
        style={valueColor ? { color: valueColor } : undefined}
      >
        {value}
      </span>
    )}
  </div>
);

// ── Details Tab ──────────────────────────────────────────────────────────────

// From GET /conversations/{id}/insights `timeline` — real per-message events,
// newest first (the API returns them oldest-first).
export const ActivityTimeline: React.FC<{
  timeline?: ConversationInsights["timeline"];
  loading?: boolean;
}> = ({ timeline, loading }) => {
  if (loading) return <p className="text-xs text-slate-400 dark:text-gray-500 py-2">Loading activity…</p>;
  const items = [...(timeline || [])].reverse();
  if (items.length === 0) return <p className="text-xs text-slate-400 dark:text-gray-500 py-2">No activity yet</p>;

  return (
    <div className="flex flex-col max-h-64 overflow-y-auto pr-1">
      {items.map((item, i) => (
        <div key={item.message_id || i} className="flex gap-3">
          <div className="flex flex-col items-center">
            <span
              className={`w-2 h-2 rounded-full shrink-0 mt-1.5 ${item.direction === "outbound" ? "bg-indigo-500" : "bg-green-500"}`}
            />
            {i < items.length - 1 && <span className="w-px flex-1 bg-slate-200 dark:bg-gray-700 my-0.5" />}
          </div>
          <div className="pb-4 min-w-0">
            <p className="text-[11px] text-slate-400 dark:text-gray-500 leading-tight">{formatDateTime(item.occurred_at)}</p>
            <p className="text-xs text-slate-800 dark:text-gray-100 font-medium leading-tight mt-0.5">{item.label}</p>
            {item.description && (
              <p className="text-xs text-slate-500 dark:text-gray-400 leading-tight mt-0.5 truncate">{item.description}</p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

const DetailsTab: React.FC<{
  leadContact: InsightsPanelProps["leadContact"];
  assignedTicket: InsightsPanelProps["assignedTicket"];
  loadingLead: boolean;
  team: InsightsPanelProps["team"];
  conv?: InsightsPanelProps["conv"];
  insights: ConversationInsights | null;
  loadingInsights: boolean;
}> = ({ leadContact, loadingLead, conv, insights, loadingInsights }) => {
  // The sender isn't always a saved contact — fall back to the conversation's
  // own title/thread id (always present) so name/phone are never blank.
  const displayName = leadContact ? getContactDisplayName(leadContact) : conv?.title || conv?.external_thread_id || "N.A.";
  const displayPhone = leadContact?.phone || conv?.external_thread_id || "N.A.";
  const initials =
    [leadContact?.first_name?.[0], leadContact?.last_name?.[0]].filter(Boolean).join("").toUpperCase() ||
    displayName?.[0]?.toUpperCase() ||
    "?";

  const { dealProgress, loadingDealProgress, updatingStage, updateStage } = useDealProgress(
    conv?.id,
    conv?.deal_stage,
    conv?.deal_progress_percent,
  );

  return (
    <>
      {/* Contact card */}
      <div className="px-4 py-3 border-b border-slate-100 dark:border-gray-800 flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-500/15 flex-shrink-0 overflow-hidden flex items-center justify-center">
          <span className="text-sm font-bold text-indigo-600 dark:text-indigo-400 select-none">{initials}</span>
        </div>
        <div>
          <div className="font-bold text-sm text-slate-800 dark:text-gray-100">{displayName}</div>
          <div className="text-xs text-slate-400 dark:text-gray-500">Last Message</div>
          <span className="inline-block mt-0.5 text-xs font-medium text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-500/15 px-1.5 py-0.5 rounded">
            {getFirstContactDate(leadContact)}
          </span>
        </div>
      </div>

      {/* Contact Info */}
      <CollapsibleSection title="Contact Info">
        {loadingLead ? (
          <div className="text-xs text-slate-400 dark:text-gray-500 py-2">Loading contact info…</div>
        ) : (
          <>
            <Row label="Full Name" value={displayName} />
            {/* No `display_name` field exists on ContactItem yet — distinct from Full Name in the design. */}
            <Row label="Display Name" value="N.A." />
            <Row label="Phone Number" value={displayPhone} />
            <Row
              label="Contact Stage"
              valueEl={
                <span className="bg-yellow-300 dark:bg-yellow-500/20 text-yellow-900 dark:text-yellow-300 text-[11px] font-semibold px-2 py-0.5 rounded">
                  New Contact
                </span>
              }
            />
            <Row
              label="Source"
              valueEl={
                <span className="bg-yellow-300 dark:bg-yellow-500/20 text-yellow-900 dark:text-yellow-300 text-[11px] font-semibold px-2 py-0.5 rounded">
                  {getContactSource(leadContact)}
                </span>
              }
            />
            <Row label="Location" value={getContactLocation(leadContact)} />
            <Row label="Email" value={leadContact?.email || "N.A."} />
            <Row label="LinkedIn ID" value={leadContact?.linkedin || "N.A."} />
          </>
        )}
      </CollapsibleSection>

      {/* Activity Timeline */}
      <CollapsibleSection title="Activity Timeline">
        <ActivityTimeline timeline={insights?.timeline} loading={loadingInsights} />
      </CollapsibleSection>

      {/* Deal Progress */}
      <CollapsibleSection title="Deal Progress">
        {loadingDealProgress && !dealProgress ? (
          <p className="text-xs text-slate-400 dark:text-gray-500 py-2">Loading deal progress…</p>
        ) : dealProgress ? (
          <>
            <Row label="Overall Progress" value={`${dealProgress.progress_percent}%`} />
            <div className="flex flex-col mt-2">
              {[...dealProgress.stages].reverse().map((stage, i, arr) => (
                <div key={stage.key} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <button
                      onClick={() => !updatingStage && !stage.current && updateStage(stage.key)}
                      disabled={updatingStage || stage.current}
                      title={stage.current ? undefined : `Move to ${stage.label}`}
                      className={`w-2.5 h-2.5 rounded-full shrink-0 mt-0.5 p-0 border-none ${
                        stage.completed ? "bg-emerald-500" : "border-2 border-slate-300 dark:border-gray-600 bg-white dark:bg-gray-800"
                      } ${stage.current ? "ring-2 ring-emerald-200 dark:ring-emerald-500/30" : "cursor-pointer"}`}
                    />
                    {i < arr.length - 1 && <span className="w-px flex-1 bg-slate-200 dark:bg-gray-700 my-0.5" />}
                  </div>
                  <div className="pb-3 min-w-0">
                    <p className={`text-xs font-medium leading-tight ${stage.current ? "text-slate-800 dark:text-gray-100" : "text-slate-500 dark:text-gray-400"}`}>
                      {stage.label}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <p className="text-[11px] text-slate-400 dark:text-gray-500">Deal tracking isn't available yet.</p>
        )}
      </CollapsibleSection>
    </>
  );
};

// ── Insight Tab ──────────────────────────────────────────────────────────────

const SparkleIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="text-indigo-500">
    <path
      d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8L12 3z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
  </svg>
);

// "2d" / "Today" — relative-time badge for Engagement Timeline rows, matching
// the design's small pill next to each row (e.g. "2d").
const formatRelativeBadge = (iso?: string | null): string | undefined => {
  if (!iso) return undefined;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return undefined;
  const days = Math.floor((Date.now() - then) / 86400000);
  if (days <= 0) return "Today";
  return `${days}d`;
};

// ── Scheduled Calls ──────────────────────────────────────────────────────────
// Real backend-backed call scheduling (conversation-call-scheduling-
// integration.md, 2026-08-05) — replaces the earlier lead.custom-backed
// placeholder. Shared by every surface that renders <ActionsTab> (inbox,
// archived preview, Leads' "View Lead Details" drawer) so scheduling,
// rescheduling, and cancelling a call behaves identically everywhere.

export type CallStatus = "scheduled" | "completed" | "cancelled";
export type CallOutcome =
  | "connected" | "no_answer" | "busy" | "voicemail" | "wrong_number" | "rescheduled" | "other";

export interface CallActor {
  user_id: string;
  name: string | null;
  email: string | null;
}

export interface ScheduledCall {
  id: string;
  conversation_id: string;
  status: CallStatus;
  scheduled_at: string;
  time_zone: string;
  purpose: string | null;
  reminder_minutes_before: number | null;
  notes: string | null;
  assigned_to: CallActor | null;
  created_by: CallActor | null;
  outcome: CallOutcome | null;
  duration_seconds: number | null;
  outcome_notes: string | null;
  completed_at: string | null;
  cancelled_at: string | null;
  cancellation_reason: string | null;
  created_at: string;
  updated_at: string;
}

export interface ScheduleCallPayload {
  scheduled_at: string;
  time_zone: string;
  purpose?: string;
  reminder_minutes_before?: number | null;
  notes?: string;
  assigned_user_id?: string;
  assigned_user_name?: string;
  assigned_user_email?: string;
}

const genIdempotencyKey = (convId: string): string => {
  const rand =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `schedule-call:${convId}:${rand}`;
};

// GET/POST/PATCH /conversations/{id}/calls[...] — next_scheduled_call /
// first_completed_call / last_completed_call come straight from the list
// endpoint's own summary fields, never inferred from the visible page.
//
// `ensureConversation` lets a caller with no conversation yet (e.g. a fresh
// lead) still schedule a call: the very first schedule attempt calls it to
// create/find one on demand, then proceeds against that id — nothing else
// about the hook's contract changes for callers that already have a
// conversation (they just never hit this path).
export function useScheduledCalls(convId?: string | null, ensureConversation?: () => Promise<string | null>) {
  const token = localStorage.getItem("authToken");
  const [resolvedConvId, setResolvedConvId] = useState<string | null | undefined>(convId);
  const [calls, setCalls] = useState<ScheduledCall[]>([]);
  const [nextScheduledCall, setNextScheduledCall] = useState<ScheduledCall | null>(null);
  const [firstCompletedCall, setFirstCompletedCall] = useState<ScheduledCall | null>(null);
  const [lastCompletedCall, setLastCompletedCall] = useState<ScheduledCall | null>(null);
  const [loadingCalls, setLoadingCalls] = useState(false);

  const { makeRequest: getCalls } = useCrmApi("/conversations", "GET", { Authorization: `Bearer ${token}` });
  const { makeRequest: postCall } = useCrmApi("/conversations", "POST", { Authorization: `Bearer ${token}` });
  const { makeRequest: patchCall } = useCrmApi("/conversations", "PATCH", { Authorization: `Bearer ${token}` });

  useEffect(() => {
    setResolvedConvId(convId);
  }, [convId]);

  const fetchCalls = async (idOverride?: string | null) => {
    const id = idOverride ?? resolvedConvId;
    if (!id) return;
    setLoadingCalls(true);
    const [response, status] = await getCalls(undefined, { limit: 50, offset: 0 }, `${id}/calls`);
    if (status >= 200 && status < 300 && response) {
      setCalls(Array.isArray(response.results) ? response.results : []);
      setNextScheduledCall(response.next_scheduled_call || null);
      setFirstCompletedCall(response.first_completed_call || null);
      setLastCompletedCall(response.last_completed_call || null);
    }
    setLoadingCalls(false);
  };

  useEffect(() => {
    if (!resolvedConvId) {
      setCalls([]);
      setNextScheduledCall(null);
      setFirstCompletedCall(null);
      setLastCompletedCall(null);
      return;
    }
    fetchCalls(resolvedConvId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedConvId]);

  // Resolves the conversation to schedule against, creating one via
  // `ensureConversation` on first use if there isn't one yet.
  const resolveConversationId = async (): Promise<string | null> => {
    if (resolvedConvId) return resolvedConvId;
    if (!ensureConversation) return null;
    const id = await ensureConversation();
    if (id) setResolvedConvId(id);
    return id || null;
  };

  const scheduleCall = async (
    payload: ScheduleCallPayload,
  ): Promise<{ ok: boolean; call?: ScheduledCall; error?: string }> => {
    const id = await resolveConversationId();
    if (!id) return { ok: false, error: "No conversation to schedule this call on yet." };
    const [response, status] = await postCall(
      { ...payload, idempotency_key: genIdempotencyKey(id) },
      undefined,
      `${id}/calls`,
    );
    if (status >= 200 && status < 300 && response?.id) {
      await fetchCalls(id);
      return { ok: true, call: response };
    }
    return { ok: false, error: response?.detail || response?.message || "Failed to schedule call" };
  };

  const rescheduleCall = async (
    callId: string,
    payload: ScheduleCallPayload,
  ): Promise<{ ok: boolean; error?: string }> => {
    if (!resolvedConvId) return { ok: false, error: "No conversation." };
    const [response, status] = await patchCall(payload, undefined, `${resolvedConvId}/calls/${callId}`);
    if (status >= 200 && status < 300) {
      await fetchCalls();
      return { ok: true };
    }
    return { ok: false, error: response?.detail || response?.message || "Failed to reschedule call" };
  };

  const cancelCall = async (callId: string, reason?: string): Promise<{ ok: boolean; error?: string }> => {
    if (!resolvedConvId) return { ok: false, error: "No conversation." };
    const [response, status] = await postCall({ reason }, undefined, `${resolvedConvId}/calls/${callId}/cancel`);
    if (status >= 200 && status < 300) {
      await fetchCalls();
      return { ok: true };
    }
    return { ok: false, error: response?.detail || response?.message || "Failed to cancel call" };
  };

  const completeCall = async (
    callId: string,
    payload: { outcome: CallOutcome; duration_seconds?: number; outcome_notes?: string },
  ): Promise<{ ok: boolean; error?: string }> => {
    if (!resolvedConvId) return { ok: false, error: "No conversation." };
    const [response, status] = await postCall(payload, undefined, `${resolvedConvId}/calls/${callId}/complete`);
    if (status >= 200 && status < 300) {
      await fetchCalls();
      return { ok: true };
    }
    return { ok: false, error: response?.detail || response?.message || "Failed to complete call" };
  };

  return {
    calls,
    nextScheduledCall,
    firstCompletedCall,
    lastCompletedCall,
    loadingCalls,
    scheduleCall,
    rescheduleCall,
    cancelCall,
    completeCall,
    refetch: fetchCalls,
  };
}

// ── Calendar + time zone helpers, shared by every Schedule Call surface ─────

export const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export const buildCalendarGrid = (viewMonth: Date): { date: Date; inMonth: boolean }[] => {
  const first = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 1);
  const mondayOffset = (first.getDay() + 6) % 7; // days to step back from the 1st to reach Monday
  const gridStart = new Date(first);
  gridStart.setDate(first.getDate() - mondayOffset);

  const daysInMonth = new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 0).getDate();
  const totalCells = Math.ceil((mondayOffset + daysInMonth) / 7) * 7;

  return Array.from({ length: totalCells }, (_, i) => {
    const date = new Date(gridStart);
    date.setDate(gridStart.getDate() + i);
    return { date, inMonth: date.getMonth() === viewMonth.getMonth() };
  });
};

export const isSameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

// 24h "HH:mm" values (what the date math needs) with a generated 12h display
// label — 09:00 to 17:00 in 30-min steps, skipping the lunch hour.
export const TIME_SLOT_VALUES = [
  "09:00", "09:30", "10:00", "10:30", "11:00", "11:30",
  "13:00", "13:30", "14:00", "14:30", "15:00", "15:30", "16:00", "16:30", "17:00",
];
export const formatSlotLabel = (hhmm: string): string => {
  const [h, m] = hhmm.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${String(h12).padStart(2, "0")}:${String(m).padStart(2, "0")} ${period}`;
};

// No fixed enum confirmed by the API doc for `purpose` (free string) — this
// is just a sensible curated list for the dropdown, snake_case to match the
// doc's own examples ("follow_up", "proposal_review").
export const CALL_PURPOSES: { value: string; label: string }[] = [
  { value: "follow_up", label: "Follow-up" },
  { value: "introduction", label: "Introduction" },
  { value: "demo", label: "Demo" },
  { value: "proposal_review", label: "Proposal Review" },
  { value: "negotiation", label: "Negotiation" },
  { value: "check_in", label: "Check-in" },
];

// The API's own allowed reminder offsets (conversation-call-scheduling-
// integration.md) — do not add values here without confirming the backend
// accepts them; `minutes: null` means "no reminder".
export const REMINDER_OPTIONS: { value: string; label: string; minutes: number | null }[] = [
  { value: "none", label: "No reminder", minutes: null },
  { value: "0", label: "At time of call", minutes: 0 },
  { value: "5", label: "5 min before", minutes: 5 },
  { value: "10", label: "10 min before", minutes: 10 },
  { value: "15", label: "15 min before", minutes: 15 },
  { value: "30", label: "30 min before", minutes: 30 },
  { value: "60", label: "1 hour before", minutes: 60 },
  { value: "1440", label: "1 day before", minutes: 1440 },
];

// The API's own allowed outcome values (conversation-call-scheduling-
// integration.md) — POST /conversations/{id}/calls/{callId}/complete.
export const CALL_OUTCOMES: { value: CallOutcome; label: string }[] = [
  { value: "connected", label: "Connected" },
  { value: "no_answer", label: "No Answer" },
  { value: "busy", label: "Busy" },
  { value: "voicemail", label: "Voicemail" },
  { value: "wrong_number", label: "Wrong Number" },
  { value: "rescheduled", label: "Rescheduled" },
  { value: "other", label: "Other" },
];

export const TIMEZONE_OPTIONS = [
  "Africa/Lagos", "Asia/Kolkata", "Asia/Dubai", "Asia/Singapore", "Europe/London",
  "America/New_York", "America/Los_Angeles", "Australia/Sydney", "UTC",
];

export const getUtcOffsetLabel = (timeZone: string): string => {
  try {
    const parts = new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName: "shortOffset" }).formatToParts(new Date());
    return (parts.find((p) => p.type === "timeZoneName")?.value || "").replace("GMT", "UTC");
  } catch {
    return "";
  }
};

export const formatTimezoneLabel = (timeZone: string): string => {
  const offset = getUtcOffsetLabel(timeZone);
  return offset ? `${timeZone.replace("/", "/ ")} ${offset}` : timeZone;
};

// Wall-clock Y/M/D/H/M in `timeZone` for a UTC instant.
const getZonedParts = (utcDate: Date, timeZone: string) => {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone, hourCycle: "h23",
    year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit",
  });
  const parts = dtf.formatToParts(utcDate).reduce((acc, p) => ({ ...acc, [p.type]: p.value }), {} as Record<string, string>);
  return { year: +parts.year, month: +parts.month, day: +parts.day, hour: +parts.hour, minute: +parts.minute };
};

// A wall-clock Y/M/D/H/M meant to represent local time IN `timeZone` -> the
// correct UTC instant, accounting for that zone's offset (incl. DST) at that
// moment. No timezone library in this project, so this uses the standard
// two-step technique: guess UTC = the wall clock taken literally, look up
// what offset `timeZone` actually has at that guess, then correct by it.
export const zonedTimeToUtcIso = (
  year: number, month: number, day: number, hour: number, minute: number, timeZone: string,
): string => {
  const guessUtcMs = Date.UTC(year, month - 1, day, hour, minute);
  const zonedAtGuess = getZonedParts(new Date(guessUtcMs), timeZone);
  const zonedAsUtcMs = Date.UTC(zonedAtGuess.year, zonedAtGuess.month - 1, zonedAtGuess.day, zonedAtGuess.hour, zonedAtGuess.minute);
  const offsetMs = zonedAsUtcMs - guessUtcMs;
  return new Date(guessUtcMs - offsetMs).toISOString().replace(/\.\d{3}Z$/, "Z");
};

// The inverse — used to pre-fill the calendar/time inputs when editing an
// existing call (scheduled_at is always UTC on the wire).
export const zonedDateAndTimeFromUtc = (isoUtc: string, timeZone: string): { date: Date; timeValue: string } => {
  const parts = getZonedParts(new Date(isoUtc), timeZone);
  return {
    date: new Date(parts.year, parts.month - 1, parts.day),
    timeValue: `${String(parts.hour).padStart(2, "0")}:${String(parts.minute).padStart(2, "0")}`,
  };
};

// Custom dropdown (not a native <select>) — a native select's own platform
// arrow can't be fully suppressed by `appearance-none` on every browser, so
// pairing it with our own absolutely-positioned chevron icon on top made the
// icon render outside the box on the browsers where the native one survives.
// This is the shared app-wide Select component instead — no native control,
// no double-arrow.
export const FieldSelect: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
}> = ({ label, value, onChange, options, placeholder }) => (
  <div className="mb-3">
    <label className="block text-xs font-medium text-slate-500 dark:text-gray-400 mb-1.5">{label}</label>
    <Select options={options} value={value} onChange={onChange} placeholder={placeholder} />
  </div>
);

// The Schedule/Reschedule Call dialog — shared by every surface that renders
// <ActionsTab>. Centered modal (not an inline panel) since two of the three
// call sites (inbox, archived preview) embed ActionsTab in a narrow ~280px
// sidebar with no room for an inline replacement panel; the Leads drawer
// renders its own bespoke inline panel instead and passes `onScheduleCall`
// to opt out of this modal (see ActionsTab below), reusing the same
// useScheduledCalls state so both stay in sync.
export const ScheduleCallModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  contactName: string;
  conversationId?: string | null;
  team?: TeamDetails[];
  defaultAssignedTo?: string | null;
  callsApi: ReturnType<typeof useScheduledCalls>;
  // When set, opens in edit/reschedule mode (PATCH) for this call instead of
  // creating a new one (POST), and offers a "Cancel Call" action.
  editingCall?: ScheduledCall | null;
}> = ({ isOpen, onClose, contactName, conversationId, team = [], defaultAssignedTo, callsApi, editingCall }) => {
  const isEditing = !!editingCall;
  const [mode, setMode] = useState<"schedule" | "complete">("schedule");
  const [assignedTo, setAssignedTo] = useState("");
  const [purpose, setPurpose] = useState(CALL_PURPOSES[0].value);
  const [reminderValue, setReminderValue] = useState("15");
  const [notes, setNotes] = useState("");
  const [timezone, setTimezone] = useState(TIMEZONE_OPTIONS[0]);
  const [viewMonth, setViewMonth] = useState(() => new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [scheduling, setScheduling] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [savingNote, setSavingNote] = useState(false);
  const [outcome, setOutcome] = useState<CallOutcome>("connected");
  const [durationMinutes, setDurationMinutes] = useState("");
  const [outcomeNotes, setOutcomeNotes] = useState("");
  const [completing, setCompleting] = useState(false);
  const { addNote } = useConversationNotes(conversationId);

  // Reset (or pre-fill from editingCall) every time the dialog is opened,
  // rather than carrying over whatever was left in state from last time.
  useEffect(() => {
    if (!isOpen) return;
    setMode("schedule");
    const pf = editingCall ? zonedDateAndTimeFromUtc(editingCall.scheduled_at, editingCall.time_zone) : null;
    setAssignedTo(editingCall?.assigned_to?.user_id || defaultAssignedTo || "");
    setPurpose(editingCall?.purpose || CALL_PURPOSES[0].value);
    setReminderValue(
      editingCall
        ? editingCall.reminder_minutes_before != null ? String(editingCall.reminder_minutes_before) : "none"
        : "15",
    );
    setNotes(editingCall?.notes || "");
    const zone =
      editingCall?.time_zone ||
      (() => {
        const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
        return TIMEZONE_OPTIONS.includes(detected) ? detected : TIMEZONE_OPTIONS[0];
      })();
    setTimezone(zone);
    const month = pf ? new Date(pf.date) : new Date();
    month.setDate(1);
    setViewMonth(month);
    setSelectedDate(pf?.date || null);
    setSelectedTime(pf?.timeValue || null);
    setOutcome("connected");
    setDurationMinutes("");
    setOutcomeNotes("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, editingCall?.id]);

  if (!isOpen) return null;

  const today = new Date();
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const grid = buildCalendarGrid(viewMonth);

  const handleSaveNote = async () => {
    if (!notes.trim() || savingNote) return;
    if (!conversationId) {
      toast.error("No conversation to attach this note to yet.");
      return;
    }
    setSavingNote(true);
    const ok = await addNote(notes.trim());
    setSavingNote(false);
    if (ok) toast.success("Note saved");
  };

  const handleComplete = async () => {
    if (!editingCall || completing) return;
    const minutes = parseFloat(durationMinutes);
    const durationSeconds = !Number.isNaN(minutes) && minutes >= 0 ? Math.min(86400, Math.round(minutes * 60)) : undefined;
    setCompleting(true);
    const result = await callsApi.completeCall(editingCall.id, {
      outcome,
      duration_seconds: durationSeconds,
      outcome_notes: outcomeNotes.trim() || undefined,
    });
    setCompleting(false);
    if (result.ok) {
      toast.success("Call marked as completed");
      onClose();
    } else {
      toast.error(result.error || "Failed to complete the call");
    }
  };

  const handleSubmit = async () => {
    if (!selectedDate || !selectedTime || scheduling) return;
    const [hour, minute] = selectedTime.split(":").map(Number);
    const scheduledAtIso = zonedTimeToUtcIso(
      selectedDate.getFullYear(), selectedDate.getMonth() + 1, selectedDate.getDate(), hour, minute, timezone,
    );
    if (new Date(scheduledAtIso).getTime() <= Date.now()) {
      toast.error("Pick a date and time in the future.");
      return;
    }
    const assignedMember = team.find((m) => m.id === assignedTo);
    const reminderOption = REMINDER_OPTIONS.find((r) => r.value === reminderValue);
    const payload: ScheduleCallPayload = {
      scheduled_at: scheduledAtIso,
      time_zone: timezone,
      purpose,
      reminder_minutes_before: reminderOption?.minutes ?? null,
      notes: notes.trim() || undefined,
      ...(assignedMember
        ? {
            assigned_user_id: assignedMember.id,
            assigned_user_name: `${assignedMember.first_name} ${assignedMember.last_name}`.trim(),
            assigned_user_email: assignedMember.email,
          }
        : {}),
    };
    setScheduling(true);
    const result =
      isEditing && editingCall ? await callsApi.rescheduleCall(editingCall.id, payload) : await callsApi.scheduleCall(payload);
    setScheduling(false);
    if (result.ok) {
      toast.success(isEditing ? "Call rescheduled" : "Call scheduled");
      onClose();
    } else {
      toast.error(result.error || "Failed to save the call");
    }
  };

  const handleCancelCall = async () => {
    if (!editingCall || cancelling) return;
    setCancelling(true);
    const result = await callsApi.cancelCall(editingCall.id);
    setCancelling(false);
    if (result.ok) {
      toast.success("Call cancelled");
      onClose();
    } else {
      toast.error(result.error || "Failed to cancel the call");
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-2 px-5 py-4 border-b border-slate-100 dark:border-gray-800 shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <Icons.Phone size={16}/>
            <h3 className="text-sm font-semibold text-slate-900 dark:text-gray-100 truncate">
              {mode === "complete" ? "Complete Call with" : isEditing ? "Reschedule Call with" : "Schedule a Call with"} {contactName}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full text-slate-400 dark:text-gray-500 hover:text-slate-700 dark:hover:text-gray-200 hover:bg-slate-100 dark:hover:bg-gray-700 transition-colors shrink-0 bg-transparent border-none cursor-pointer"
          >
            <XIcon size={18} />
          </button>
        </div>

        {isEditing && (
          <div className="flex gap-1 mx-5 mt-3 mb-1 bg-slate-50 dark:bg-gray-900 rounded-lg p-1 shrink-0">
            <button
              onClick={() => setMode("schedule")}
              className={`flex-1 py-1.5 rounded-md text-xs font-semibold transition-colors border-none cursor-pointer ${mode === "schedule" ? "bg-white dark:bg-gray-700 shadow-sm text-slate-900 dark:text-gray-100" : "bg-transparent text-slate-400 dark:text-gray-500"}`}
            >
              Reschedule
            </button>
            <button
              onClick={() => setMode("complete")}
              className={`flex-1 py-1.5 rounded-md text-xs font-semibold transition-colors border-none cursor-pointer ${mode === "complete" ? "bg-white dark:bg-gray-700 shadow-sm text-slate-900 dark:text-gray-100" : "bg-transparent text-slate-400 dark:text-gray-500"}`}
            >
              Mark as Completed
            </button>
          </div>
        )}

        <div className="flex-1 overflow-y-auto">
          {mode === "complete" ? (
            <CollapsibleSection title="Call Outcome">
              <FieldSelect label="Outcome" value={outcome} onChange={(v) => setOutcome(v as CallOutcome)} options={CALL_OUTCOMES} />
              <label className="block text-xs font-medium text-slate-500 dark:text-gray-400 mb-1.5">Duration (minutes, optional)</label>
              <input
                type="number"
                min={0}
                max={1440}
                value={durationMinutes}
                onChange={(e) => setDurationMinutes(e.target.value)}
                placeholder="e.g. 7"
                className="w-full border border-slate-200 dark:border-gray-700 dark:bg-gray-900 rounded-lg px-3 py-2.5 text-sm text-slate-800 dark:text-gray-100 outline-none focus:border-indigo-400 mb-3"
              />
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-medium text-slate-500 dark:text-gray-400">Outcome Notes</label>
                <span className="text-[11px] text-slate-400 dark:text-gray-500">{outcomeNotes.length}/300</span>
              </div>
              <textarea
                value={outcomeNotes}
                maxLength={300}
                onChange={(e) => setOutcomeNotes(e.target.value)}
                placeholder="What happened on the call?"
                rows={3}
                className="w-full border border-slate-200 dark:border-gray-700 dark:bg-gray-900 rounded-lg px-3 py-2.5 text-sm text-slate-700 dark:text-gray-200 outline-none focus:border-indigo-400 resize-none"
              />
            </CollapsibleSection>
          ) : (
          <>
          <CollapsibleSection title="Add Call Details">
            <FieldSelect
              label="Contact Assigned to"
              value={assignedTo}
              onChange={setAssignedTo}
              placeholder="Select a team member"
              options={team.map((m) => ({ value: m.id, label: `${m.first_name} ${m.last_name}`.trim() }))}
            />
            <FieldSelect label="Call Purpose (Optional)" value={purpose} onChange={setPurpose} options={CALL_PURPOSES} />
            <FieldSelect label="Send Reminder" value={reminderValue} onChange={setReminderValue} options={REMINDER_OPTIONS} />
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-medium text-slate-500 dark:text-gray-400">Call Notes</label>
              <span className="text-[11px] text-slate-400 dark:text-gray-500">{notes.length}/300</span>
            </div>
            <textarea
              value={notes}
              maxLength={300}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Your note"
              rows={3}
              className="w-full border border-slate-200 dark:border-gray-700 dark:bg-gray-900 rounded-lg px-3 py-2.5 text-sm text-slate-700 dark:text-gray-200 outline-none focus:border-indigo-400 resize-none mb-2"
            />
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => toast.info("AI note suggestions are coming soon.")}
                className="flex items-center gap-1.5 text-sm font-medium text-indigo-600 dark:text-indigo-400 bg-transparent border-none cursor-pointer p-0 hover:text-indigo-700 dark:hover:text-indigo-300"
              >
                <SparklesIcon size={14} />
                Suggest
              </button>
              <button
                onClick={handleSaveNote}
                disabled={!notes.trim() || savingNote}
                className="px-3.5 py-1.5 rounded-lg bg-slate-100 dark:bg-gray-700 hover:bg-slate-200 dark:hover:bg-gray-600 text-slate-700 dark:text-gray-200 text-xs font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {savingNote ? "Saving…" : "Save as note"}
              </button>
            </div>
          </CollapsibleSection>

          <CollapsibleSection title="Select Call Date">
            <div className="flex items-center justify-between mb-3">
              <button
                onClick={() => setViewMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1))}
                className="p-1.5 rounded-full border border-slate-200 dark:border-gray-700 text-slate-500 dark:text-gray-400 hover:bg-slate-50 dark:hover:bg-gray-700 bg-white dark:bg-gray-800 cursor-pointer"
              >
                <ChevronLeft size={15} />
              </button>
              <span className="text-sm font-bold text-slate-900 dark:text-gray-100">
                {viewMonth.toLocaleDateString(undefined, { month: "long", year: "numeric" })}
              </span>
              <button
                onClick={() => setViewMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1))}
                className="p-1.5 rounded-full border border-slate-200 dark:border-gray-700 text-slate-500 dark:text-gray-400 hover:bg-slate-50 dark:hover:bg-gray-700 bg-white dark:bg-gray-800 cursor-pointer"
              >
                <ChevronRight size={15} />
              </button>
            </div>
            <div className="grid grid-cols-7 gap-1 text-center mb-1">
              {WEEKDAY_LABELS.map((d) => (
                <span key={d} className="text-[11px] font-medium text-slate-400 dark:text-gray-500 py-1">{d}</span>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {grid.map(({ date, inMonth }, i) => {
                const isSelected = !!selectedDate && isSameDay(date, selectedDate);
                const isToday = isSameDay(date, today);
                const isPast = date < startOfToday;
                return (
                  <button
                    key={i}
                    disabled={!inMonth || isPast}
                    onClick={() => setSelectedDate(date)}
                    className={`aspect-square rounded-full text-xs font-medium transition-colors ${
                      !inMonth || isPast
                        ? "text-slate-300 dark:text-gray-600 cursor-default"
                        : isSelected
                          ? "bg-indigo-600 text-white"
                          : isToday
                            ? "border border-indigo-400 dark:border-indigo-500 text-slate-800 dark:text-gray-100 hover:bg-slate-50 dark:hover:bg-gray-700"
                            : "text-slate-700 dark:text-gray-300 hover:bg-slate-50 dark:hover:bg-gray-700"
                    }`}
                  >
                    {date.getDate()}
                  </button>
                );
              })}
            </div>
          </CollapsibleSection>

          <CollapsibleSection title="Select Time">
            <FieldSelect
              label="Time Zone"
              value={timezone}
              onChange={setTimezone}
              options={TIMEZONE_OPTIONS.map((tz) => ({ value: tz, label: formatTimezoneLabel(tz) }))}
            />
            <div className="grid grid-cols-3 gap-2 mt-1">
              {TIME_SLOT_VALUES.map((slot) => (
                <button
                  key={slot}
                  onClick={() => setSelectedTime(slot)}
                  className={`py-2 rounded-lg text-xs font-medium border transition-colors ${
                    selectedTime === slot
                      ? "bg-indigo-600 border-indigo-600 text-white"
                      : "border-slate-200 dark:border-gray-700 text-slate-700 dark:text-gray-300 hover:bg-slate-50 dark:hover:bg-gray-700"
                  }`}
                >
                  {formatSlotLabel(slot)}
                </button>
              ))}
            </div>
          </CollapsibleSection>
          </>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 px-5 py-3.5 border-t border-slate-100 dark:border-gray-800 shrink-0">
          {isEditing && mode === "schedule" ? (
            <button
              onClick={handleCancelCall}
              disabled={cancelling}
              className="px-3 py-2 rounded-lg text-sm font-semibold text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 bg-transparent border-none cursor-pointer disabled:opacity-50"
            >
              {cancelling ? "Cancelling…" : "Cancel Call"}
            </button>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg border border-slate-200 dark:border-gray-700 text-sm font-semibold text-slate-700 dark:text-gray-200 hover:bg-slate-50 dark:hover:bg-gray-700 bg-white dark:bg-gray-800 transition-colors"
            >
              Close
            </button>
            {mode === "complete" ? (
              <button
                onClick={handleComplete}
                disabled={completing}
                className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {completing ? "Saving…" : "Mark Completed"}
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={!selectedDate || !selectedTime || scheduling}
                className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {scheduling ? "Saving…" : isEditing ? "Save Changes" : "Schedule Call"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const EngagementTimelineRow: React.FC<{
  label: string;
  value?: string;
  channel?: string;
  badge?: string;
  onClick?: () => void;
}> = ({ label, value, channel, badge, onClick }) => {
  const content = (
    <>
      <div className="min-w-0">
        <p className="text-xs font-medium text-slate-800 dark:text-gray-100 leading-tight">{label}</p>
        <p className="text-[11px] text-slate-400 dark:text-gray-500 leading-tight mt-0.5 truncate">{value || "Not set yet"}</p>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        {channel && (
          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 dark:bg-gray-700 text-slate-500 dark:text-gray-400">{channel}</span>
        )}
        {badge && (
          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-indigo-50 dark:bg-indigo-500/15 text-indigo-600 dark:text-indigo-400">{badge}</span>
        )}
      </div>
    </>
  );
  if (onClick) {
    return (
      <button
        onClick={onClick}
        className="w-full flex items-center justify-between gap-2 py-1.5 bg-transparent border-none cursor-pointer text-left hover:opacity-70 transition-opacity"
      >
        {content}
      </button>
    );
  }
  return <div className="flex items-center justify-between gap-2 py-1.5">{content}</div>;
};

// seconds -> "7 min" / "1h 30min" / "45s". No API field for this shape, so
// this is purely a display formatter, not fabricated data.
const formatDuration = (seconds?: number | null): string => {
  if (seconds == null) return "N.A.";
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const mins = Math.round(seconds / 60);
  if (mins < 60) return `${mins}min`;
  const hours = Math.floor(mins / 60);
  const rem = mins % 60;
  return rem ? `${hours}h ${rem}min` : `${hours}h`;
};

export interface ContactPreferences {
  preferred_channel: "whatsapp" | "rcs" | "email" | "phone";
  preferred_time_window?: { start: string; end: string; time_zone: string } | null;
}

const PREFERRED_CHANNEL_LABEL: Record<string, string> = {
  whatsapp: "WhatsApp",
  rcs: "RCS",
  email: "Email",
  phone: "Phone",
};

export const InsightDataTab: React.FC<{
  insights: ConversationInsights | null;
  loadingInsights: boolean;
  // Callers with a real, editable tag list (e.g. a lead/contact record) pass
  // these to override the read-only conversation-summary tags and enable the
  // "New Tag" modal below. Without `onSaveTags`, the tag list stays read-only
  // exactly as before.
  tags?: string[];
  onSaveTags?: (tags: string[]) => Promise<boolean> | void;
  // lead.custom.contact_preferences — only leads carry this custom-backed
  // field today, so it's optional and falls back to "N.A." without it.
  contactPreferences?: ContactPreferences | null;
}> = ({ insights, loadingInsights, tags: tagsOverride, onSaveTags, contactPreferences }) => {
  const tags = tagsOverride ?? insights?.summary?.tags ?? [];
  const engagement = insights?.engagement;
  const [showTagModal, setShowTagModal] = useState(false);

  const handleSaveTags = async (nextTags: string[]): Promise<boolean> => {
    if (!onSaveTags) return false;
    const result = await onSaveTags(nextTags);
    return result !== false;
  };

  return (
    <>
      {/* Summary + Conversation Tags */}
      <CollapsibleSection title="Summary" icon={<SparkleIcon />}>
        {loadingInsights ? (
          <p className="text-xs text-slate-400 dark:text-gray-500 py-1">Loading summary…</p>
        ) : (
          <p className="text-xs text-slate-500 dark:text-gray-400 leading-relaxed mb-3">
            {insights?.summary?.text || "No summary available yet."}
          </p>
        )}
        <p className="text-xs text-slate-400 dark:text-gray-500 mb-1.5">Conversation Tags</p>
        {tags.length === 0 ? (
          <p className="text-xs text-slate-400 dark:text-gray-500 mb-2">No tags yet</p>
        ) : (
          <div className="flex flex-wrap gap-1.5 mb-2">
            {tags.map((tag) => (
              <span
                key={tag}
                className="text-[11px] font-semibold px-2 py-0.5 rounded bg-indigo-50 dark:bg-indigo-500/15 text-indigo-600 dark:text-indigo-400 border border-indigo-100 dark:border-indigo-500/30"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
        {onSaveTags && (
          <button
            onClick={() => setShowTagModal(true)}
            className="flex items-center justify-between gap-2 px-3 py-2 bg-slate-50 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg cursor-pointer w-full text-xs font-semibold text-slate-600 dark:text-gray-300 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 hover:border-indigo-300 dark:hover:border-indigo-500/40 transition-colors"
          >
            New Tag <Icons.Plus size={13} />
          </button>
        )}
      </CollapsibleSection>

      {/* Engagement Insight */}
      <CollapsibleSection title="Engagement Insight">
        {loadingInsights ? (
          <p className="text-xs text-slate-400 dark:text-gray-500 py-1">Loading engagement data…</p>
        ) : (
          <>
            <Row
              label="First Message"
              value={engagement?.first_message_at ? formatDateTime(engagement.first_message_at) : "N.A."}
            />
            <Row
              label="Last Active"
              value={engagement?.last_active_at ? formatDateTime(engagement.last_active_at) : "N.A."}
            />
            <Row label="Total Sessions" value={engagement?.total_sessions?.toString() ?? "N.A."} />
            {engagement?.session_metrics && (
              <>
                <Row
                  label="Sessions Completed"
                  value={engagement.session_metrics.completed.toString()}
                />
                <Row
                  label="Sessions Active"
                  valueColor={engagement.session_metrics.active > 0 ? "#16a34a" : undefined}
                  value={engagement.session_metrics.active.toString()}
                />
                <Row
                  label="Customer-Initiated"
                  value={formatSessionBreakdown(engagement.session_metrics.customer_initiated)}
                />
                <Row
                  label="Business-Initiated"
                  value={formatSessionBreakdown(engagement.session_metrics.business_initiated)}
                />
              </>
            )}
            <Row label="Messages Sent" value={engagement?.messages_sent?.toString() ?? "N.A."} />
            <Row label="Messages Received" value={engagement?.messages_received?.toString() ?? "N.A."} />
            <Row label="Avg. Response Time" value={formatDuration(engagement?.avg_response_seconds)} />
            <Row
              label="Response Rate"
              valueColor={engagement?.response_rate != null ? "#16a34a" : undefined}
              value={engagement?.response_rate != null ? `${engagement.response_rate}%` : "N.A."}
            />
          </>
        )}
      </CollapsibleSection>

      {/* Contact Preferences — custom-backed on the lead record (lead.custom.contact_preferences), not derived from insights. */}
      <CollapsibleSection title="Contact Preferences">
        <Row
          label="Preferred Channel"
          value={
            contactPreferences?.preferred_channel
              ? PREFERRED_CHANNEL_LABEL[contactPreferences.preferred_channel] || contactPreferences.preferred_channel
              : "N.A."
          }
        />
        <Row
          label="Preferred Time"
          value={
            contactPreferences?.preferred_time_window
              ? `${contactPreferences.preferred_time_window.start}–${contactPreferences.preferred_time_window.end} (${contactPreferences.preferred_time_window.time_zone})`
              : "N.A."
          }
        />
      </CollapsibleSection>

      {onSaveTags && (
        <NewTagModal
          isOpen={showTagModal}
          onClose={() => setShowTagModal(false)}
          existingTags={tags}
          onSave={handleSaveTags}
        />
      )}
    </>
  );
};

// ── Actions Tab ───────────────────────────────────────────────────────────────

// "18 Aug 2026, 1:00 PM" — display formatter for a scheduled/completed call's timestamp.
const formatCallWhen = (call: ScheduledCall): string => {
  try {
    return new Date(call.scheduled_at).toLocaleString(undefined, {
      day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch {
    return call.scheduled_at;
  }
};

export const ActionsTab: React.FC<{
  insights: ConversationInsights | null;
  loadingInsights: boolean;
  conversationId?: string | null;
  // For the Schedule Call modal's header + "Contact Assigned to" dropdown.
  contactName?: string;
  team?: TeamDetails[];
  defaultAssignedTo?: string | null;
  // Lift this (via useScheduledCalls) when a caller renders its own bespoke
  // scheduling UI elsewhere (e.g. Leads' inline panel, via onScheduleCall
  // below) instead of this tab's own modal — keeps both in sync off one
  // shared fetch/mutation state rather than two independent ones that could
  // go stale relative to each other.
  callsApi?: ReturnType<typeof useScheduledCalls>;
  // Fixed, always-executable actions — per the Segments/Conversation API doc,
  // `next_best_actions` is display-only text with no stable action id, so
  // real buttons live here instead. Send Message / Alert Owner need lead
  // context this tab doesn't always have, so they stay optional; Schedule a
  // Call only needs a conversation, so it's always shown.
  onSendMessage?: () => void;
  // Override: render the scheduling UI elsewhere (e.g. Leads' inline panel
  // that replaces the conversation thread) instead of this tab's own modal.
  onScheduleCall?: (call?: ScheduledCall | null) => void;
  onAlertOwner?: () => void;
}> = ({
  insights, loadingInsights, conversationId, contactName = "this contact", team = [], defaultAssignedTo,
  callsApi: callsApiProp, onSendMessage, onScheduleCall, onAlertOwner,
}) => {
  const signals = insights?.signals;
  const conversationSignals = signals?.conversation_signals || [];
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [editingCall, setEditingCall] = useState<ScheduledCall | null>(null);
  const { notes, totalNotes, loadingNotes, addNote } = useConversationNotes(conversationId);

  // If the parent lifted its own instance (passed via callsApi), don't also
  // fetch here — pass null so this internal hook stays inert.
  const ownCallsApi = useScheduledCalls(callsApiProp ? null : conversationId);
  const callsApi = callsApiProp || ownCallsApi;
  const { nextScheduledCall, lastCompletedCall, firstCompletedCall } = callsApi;

  const handleSaveNote = async (description: string): Promise<boolean> => {
    if (!conversationId) {
      toast.error("No conversation to attach this note to yet.");
      return false;
    }
    const ok = await addNote(description);
    if (ok) toast.success("Note saved.");
    return ok;
  };

  const openScheduleUi = (call?: ScheduledCall | null) => {
    if (onScheduleCall) {
      onScheduleCall(call);
      return;
    }
    setEditingCall(call || null);
    setShowScheduleModal(true);
  };

  return (
    <>
      <CollapsibleSection title="Contact Signals">
        {loadingInsights ? (
          <p className="text-xs text-slate-400 dark:text-gray-500 py-1">Loading signals…</p>
        ) : (
          <>
            <p className="text-xs text-slate-400 dark:text-gray-500 mb-1">Avg. Intent Score</p>
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-bold text-slate-800 dark:text-gray-100">{signals?.intent_label || "Unknown"}</span>
              <span className="text-xs text-slate-400 dark:text-gray-500">
                {signals?.intent_score != null ? `${signals.intent_score}/100` : "N.A."}
              </span>
            </div>
            <div className="bg-slate-200 dark:bg-gray-700 rounded-full h-1.5 mb-4">
              <div
                className={`h-full rounded-full ${signals?.intent_score != null ? "bg-green-500" : ""}`}
                style={{ width: signals?.intent_score != null ? `${signals.intent_score}%` : "0%" }}
              />
            </div>

            <p className="text-xs text-slate-400 dark:text-gray-500 mb-1.5">Conversation Signals</p>
            {conversationSignals.length === 0 ? (
              <p className="text-xs text-slate-400 dark:text-gray-500 mb-4">No signals detected yet</p>
            ) : (
              <div className="mb-4">
                {conversationSignals.map((signal) => (
                  <GreenDot key={signal} label={signal} />
                ))}
              </div>
            )}
          </>
        )}
      </CollapsibleSection>

      <CollapsibleSection title="Next Best Action">
        <div className="text-[11px] text-slate-400 dark:text-gray-500 mb-2">Next best actions for this lead</div>
        <div className="space-y-1.5">
          {onSendMessage && (
            <button
              onClick={onSendMessage}
              className="w-full flex items-center justify-between gap-2 px-3.5 py-2.5 bg-slate-50 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg cursor-pointer font-semibold text-sm text-slate-800 dark:text-gray-100 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 hover:border-indigo-300 dark:hover:border-indigo-500/40 transition-colors"
            >
              <span className="flex items-center gap-2">
                <Icons.Send size={14} />
                Send Message
              </span>
              <Icons.ArrowRight size={16} />
            </button>
          )}
          <button
            onClick={() => openScheduleUi(nextScheduledCall)}
            className="w-full flex items-center justify-between gap-2 px-3.5 py-2.5 bg-slate-50 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg cursor-pointer font-semibold text-sm text-slate-800 dark:text-gray-100 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 hover:border-indigo-300 dark:hover:border-indigo-500/40 transition-colors"
          >
            <span className="flex items-center gap-2">
              <Icons.Phone size={14} />
              {nextScheduledCall ? "Reschedule Call" : "Schedule a Call"}
            </span>
            <Icons.ArrowRight size={16} />
          </button>
          {onAlertOwner && (
            <button
              onClick={onAlertOwner}
              className="w-full flex items-center justify-between gap-2 px-3.5 py-2.5 bg-slate-50 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg cursor-pointer font-semibold text-sm text-slate-800 dark:text-gray-100 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 hover:border-indigo-300 dark:hover:border-indigo-500/40 transition-colors"
            >
              <span className="flex items-center gap-2">
                <BellIcon size={14} className="text-slate-400 dark:text-gray-500" />
                Alert Lead Owner
              </span>
              <Icons.ArrowRight size={16} />
            </button>
          )}
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="Engagement Timeline">
        <EngagementTimelineRow
          label="Next Scheduled Call"
          value={nextScheduledCall ? formatCallWhen(nextScheduledCall) : undefined}
          channel="Call"
          badge={nextScheduledCall ? undefined : "Not set"}
          onClick={nextScheduledCall ? () => openScheduleUi(nextScheduledCall) : undefined}
        />
        <EngagementTimelineRow
          label="Last Call"
          value={lastCompletedCall ? formatCallWhen(lastCompletedCall) : undefined}
          channel="Call"
        />
        <EngagementTimelineRow
          label="First Message"
          value={insights?.engagement?.first_message_at ? formatDateTime(insights.engagement.first_message_at) : undefined}
          channel="Message"
          badge={formatRelativeBadge(insights?.engagement?.first_message_at)}
        />
        <EngagementTimelineRow
          label="First Call"
          value={firstCompletedCall ? formatCallWhen(firstCompletedCall) : undefined}
          channel="Call"
        />
      </CollapsibleSection>

      <CollapsibleSection title="Add Engagement Notes">
        <div className="text-[11px] text-slate-400 dark:text-gray-500 mb-2">
          Add notes for your reference{totalNotes > 0 ? ` (${totalNotes})` : ""}
        </div>
        <button
          onClick={() => setShowNoteModal(true)}
          disabled={!conversationId}
          title={conversationId ? undefined : "No conversation to attach this note to yet"}
          className="flex items-center justify-between gap-2 px-3.5 py-2.5 bg-slate-50 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg cursor-pointer w-full text-sm font-semibold text-slate-600 dark:text-gray-300 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 hover:border-indigo-300 dark:hover:border-indigo-500/40 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          New Note <Icons.Plus size={16} />
        </button>

        {loadingNotes ? (
          <p className="text-xs text-slate-400 dark:text-gray-500 py-2">Loading notes…</p>
        ) : notes.length > 0 && (
          <div className="mt-3 space-y-2 max-h-56 overflow-y-auto pr-1">
            {notes.map((note) => (
              <div key={note.id} className="bg-slate-50 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg p-2.5">
                <p className="text-xs text-slate-700 dark:text-gray-200 leading-relaxed">{note.description}</p>
                <p className="text-[10px] text-slate-400 dark:text-gray-500 mt-1">
                  {formatDateTime(note.created_at)}
                  {note.author?.email ? ` · ${note.author.email}` : ""}
                </p>
              </div>
            ))}
          </div>
        )}
      </CollapsibleSection>

      <NewNoteModal isOpen={showNoteModal} onClose={() => setShowNoteModal(false)} onSave={handleSaveNote} />

      {!onScheduleCall && (
        <ScheduleCallModal
          isOpen={showScheduleModal}
          onClose={() => { setShowScheduleModal(false); setEditingCall(null); }}
          contactName={contactName}
          conversationId={conversationId}
          team={team}
          defaultAssignedTo={defaultAssignedTo}
          callsApi={callsApi}
          editingCall={editingCall}
        />
      )}
    </>
  );
};

// ── Main Panel ───────────────────────────────────────────────────────────────

export const InsightsPanel: React.FC<InsightsPanelProps> = ({
  activeInsightTab,
  onTabChange,
  leadContact,
  assignedTicket,
  loadingLead = false,
  team = [],
  conv,
}) => {
  const { insights, loadingInsights } = useConversationInsights(conv?.id);

  return (
  <div className="w-[280px] min-w-[240px] bg-white dark:bg-gray-900 border-l border-[#e8ecf4] dark:border-gray-800 flex flex-col overflow-hidden">
    {/* Header */}
    <div className="px-4 pt-3.5 pb-2.5 border-b border-slate-100 dark:border-gray-800 bg-[#F4FAF3] dark:bg-gray-800/60">
      <div className="flex items-center gap-1.5 mb-3">
        <div className="relative flex items-center justify-center">

          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M12 2C12.901 2 13.774 2.12 14.605 2.344C14.0859 2.61661 13.658 3.03539 13.3743 3.54855C13.0906 4.06172 12.9635 4.64678 13.0087 5.2314C13.0539 5.81602 13.2695 6.37458 13.6287 6.83802C13.9879 7.30145 14.4751 7.64946 15.03 7.839L15.408 7.968C15.5528 8.01735 15.6844 8.09926 15.7926 8.20745C15.9007 8.31563 15.9827 8.44719 16.032 8.592L16.162 8.97C16.3519 9.52433 16.6999 10.0109 17.1631 10.3697C17.6264 10.7285 18.1845 10.9438 18.7687 10.989C19.3529 11.0342 19.9376 10.9073 20.4505 10.6241C20.9634 10.3408 21.3821 9.91353 21.655 9.395C21.8841 10.2444 22.0001 11.1203 22 12C22 17.523 17.523 22 12 22C10.11 22 8.34001 21.475 6.83201 20.563L3.80001 21.454C3.62593 21.5052 3.44126 21.5086 3.26542 21.4638C3.08959 21.4189 2.92908 21.3276 2.80077 21.1992C2.67246 21.0709 2.58108 20.9104 2.53625 20.7346C2.49142 20.5587 2.49479 20.3741 2.54601 20.2L3.43801 17.168C2.49506 15.6093 1.99769 13.8218 2.00001 12C2.00001 6.477 6.47701 2 12 2ZM8.50001 10.5C8.10218 10.5 7.72065 10.658 7.43935 10.9393C7.15804 11.2206 7.00001 11.6022 7.00001 12C7.00001 12.3978 7.15804 12.7794 7.43935 13.0607C7.72065 13.342 8.10218 13.5 8.50001 13.5C8.89783 13.5 9.27936 13.342 9.56067 13.0607C9.84197 12.7794 10 12.3978 10 12C10 11.6022 9.84197 11.2206 9.56067 10.9393C9.27936 10.658 8.89783 10.5 8.50001 10.5ZM15.5 10.5C15.1022 10.5 14.7207 10.658 14.4393 10.9393C14.158 11.2206 14 11.6022 14 12C14 12.3978 14.158 12.7794 14.4393 13.0607C14.7207 13.342 15.1022 13.5 15.5 13.5C15.8978 13.5 16.2794 13.342 16.5607 13.0607C16.842 12.7794 17 12.3978 17 12C17 11.6022 16.842 11.2206 16.5607 10.9393C16.2794 10.658 15.8978 10.5 15.5 10.5ZM19 1C19.2086 1.00008 19.4119 1.06539 19.5816 1.18679C19.7512 1.30818 19.8786 1.47959 19.946 1.677L20.076 2.055C20.376 2.934 21.066 3.625 21.946 3.925L22.323 4.054C22.5202 4.12157 22.6914 4.24908 22.8125 4.41869C22.9337 4.58831 22.9988 4.79155 22.9988 5C22.9988 5.20845 22.9337 5.41169 22.8125 5.58131C22.6914 5.75092 22.5202 5.87843 22.323 5.946L21.945 6.076C21.066 6.376 20.375 7.066 20.075 7.946L19.946 8.323C19.8784 8.5202 19.7509 8.69134 19.5813 8.81252C19.4117 8.93369 19.2085 8.99883 19 8.99883C18.7916 8.99883 18.5883 8.93369 18.4187 8.81252C18.2491 8.69134 18.1216 8.5202 18.054 8.323L17.924 7.945C17.7759 7.51114 17.5303 7.11699 17.2062 6.79283C16.882 6.46866 16.4879 6.2231 16.054 6.075L15.677 5.946C15.4798 5.87843 15.3087 5.75092 15.1875 5.58131C15.0663 5.41169 15.0012 5.20845 15.0012 5C15.0012 4.79155 15.0663 4.58831 15.1875 4.41869C15.3087 4.24908 15.4798 4.12157 15.677 4.054L16.055 3.924C16.934 3.624 17.625 2.934 17.925 2.054L18.054 1.677C18.1214 1.47959 18.2488 1.30818 18.4184 1.18679C18.5881 1.06539 18.7914 1.00008 19 1Z" fill="#4B4141" />
            <path d="M8.51172 15.8539C8.51172 15.8539 9.45199 18.0078 12.075 18.0078C14.698 18.0078 15.5885 15.832 15.5885 15.832" stroke="white" stroke-linecap="round" />
          </svg>

        </div>
        <span className="font-extrabold text-sm text-slate-800 dark:text-gray-100">ConvoAI Insights</span>
      </div>
      <div className="flex gap-1 bg-white dark:bg-gray-800 rounded-lg p-[3px]">
        {(["Actions", "Insight", "Details"] as InsightTab[]).map(tab => (
          <button
            key={tab}
            onClick={() => onTabChange(tab)}
            className={`flex-1 py-1.5 rounded-lg border-none cursor-pointer font-semibold text-xs transition-all duration-150 ${activeInsightTab === tab
              ? "bg-white dark:bg-gray-700 text-slate-900 dark:text-gray-100 shadow-sm"
              : "bg-transparent text-slate-400 dark:text-gray-500"
              }`}
          >
            {tab}
          </button>
        ))}
      </div>
    </div>

    {/* Body */}
    <div className="flex-1 overflow-y-auto">
      {activeInsightTab === "Details" && (
        <DetailsTab
          leadContact={leadContact}
          assignedTicket={assignedTicket}
          loadingLead={loadingLead}
          team={team}
          conv={conv}
          insights={insights}
          loadingInsights={loadingInsights}
        />
      )}
      {activeInsightTab === "Insight" && (
        <InsightDataTab insights={insights} loadingInsights={loadingInsights} />
      )}
      {activeInsightTab === "Actions" && (
        <ActionsTab insights={insights} loadingInsights={loadingInsights} conversationId={conv?.id} />
      )}
    </div>
  </div>
  );
};
